/**
 * Profile completion: field order and empty checks aligned with user.entity and UpdateUserProfileDto.
 */

/** Order: critical first (allergies), then vitals, meds, then demography to reduce overwhelm. */
export const PROFILE_FIELD_ORDER = [
  "allergies",
  "height",
  "weight",
  "medications",
  "conditions",
  "diseaseBurdens",
  "bloodGroup",
  "gender",
  "dateOfBirth",
  "emergencyContact",
] as const;

export type ProfileFieldKey = (typeof PROFILE_FIELD_ORDER)[number];

export interface ApiProfile {
  fullname?: string;
  dateOfBirth?: string | Date | null;
  height?: string | null;
  weight?: string | null;
  bloodGroup?: string | null;
  gender?: string | null;
  allergies?: string[] | null;
  conditions?: string[] | null;
  medications?: string[] | null;
  diseaseBurdens?: string[] | null;
  emergencyContact?: {
    name?: string;
    relationship?: string;
    phone?: string;
    location?: string;
  } | null;
}

function isStringEmpty(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v !== "string") return true;
  return v.trim() === "";
}

function isArrayEmpty(v: unknown): boolean {
  if (v == null) return true;
  if (!Array.isArray(v)) return true;
  return v.length === 0;
}

function isEmergencyContactEmpty(ec: ApiProfile["emergencyContact"]): boolean {
  if (!ec || typeof ec !== "object") return true;
  const name = (ec as { name?: string }).name;
  const relationship = (ec as { relationship?: string }).relationship;
  const phone = (ec as { phone?: string }).phone;
  const location = (ec as { location?: string }).location;
  return (
    isStringEmpty(name) &&
    isStringEmpty(relationship) &&
    isStringEmpty(phone) &&
    isStringEmpty(location)
  );
}

export function isEmpty(profile: ApiProfile, key: ProfileFieldKey): boolean {
  const v = profile[key as keyof ApiProfile];
  switch (key) {
    case "dateOfBirth":
      if (v instanceof Date) return false;
      return isStringEmpty(v) || (typeof v === "string" && !v.trim());
    case "height":
    case "weight":
    case "bloodGroup":
    case "gender":
      return isStringEmpty(v);
    case "allergies":
    case "conditions":
    case "medications":
    case "diseaseBurdens":
      return isArrayEmpty(v);
    case "emergencyContact":
      return isEmergencyContactEmpty(profile.emergencyContact ?? null);
    default:
      return true;
  }
}

export function getEmptyFields(profile: ApiProfile | null): ProfileFieldKey[] {
  if (!profile) return [];
  return PROFILE_FIELD_ORDER.filter((key) => isEmpty(profile, key));
}

/**
 * Gate for the "Complete your profile" nudge: it should reappear on every
 * new login, or after 24 hours - not on every page refresh within the same
 * login. `resetProfileCompletionGate` is called from the login/register/
 * OAuth success paths; `isProfileCompletionDue` is checked once per
 * MainLayout mount.
 */
export const PROFILE_COMPLETION_LAST_SHOWN_KEY = "@ProfileCompletionLastShown";
export const PROFILE_COMPLETION_RESHOW_INTERVAL_MS = 24 * 60 * 60 * 1000;

export function resetProfileCompletionGate(): void {
  try {
    localStorage.removeItem(PROFILE_COMPLETION_LAST_SHOWN_KEY);
  } catch {
    // localStorage may be unavailable (e.g. private browsing) - safe to skip
  }
}

/** Returns true (and records "shown now") if the nudge is due - a new login
 * (no record yet) or 24+ hours since it was last shown. */
export function isProfileCompletionDue(): boolean {
  try {
    const lastShownRaw = localStorage.getItem(PROFILE_COMPLETION_LAST_SHOWN_KEY);
    const lastShown = lastShownRaw ? Number(lastShownRaw) : 0;
    const due =
      !lastShownRaw ||
      Number.isNaN(lastShown) ||
      Date.now() - lastShown >= PROFILE_COMPLETION_RESHOW_INTERVAL_MS;
    if (due) {
      localStorage.setItem(PROFILE_COMPLETION_LAST_SHOWN_KEY, String(Date.now()));
    }
    return due;
  } catch {
    // localStorage unavailable - default to showing rather than hiding
    return true;
  }
}

export const PROFILE_FIELD_LABELS: Record<ProfileFieldKey, string> = {
  dateOfBirth: "Date of birth",
  height: "Height",
  weight: "Weight",
  bloodGroup: "Blood group",
  gender: "Gender",
  allergies: "Allergies",
  conditions: "Medical conditions",
  medications: "Medications",
  diseaseBurdens: "Disease burdens",
  emergencyContact: "Emergency contact",
};

/** Placeholders for comma-separated list fields (and optional height/weight). */
export const PROFILE_FIELD_PLACEHOLDERS: Partial<Record<ProfileFieldKey, string>> = {
  allergies: "e.g. pollen, smoke, milk, soy",
  conditions: "e.g. diabetes, asthma, hypertension",
  medications: "e.g. milk of magnesia, metformin",
  diseaseBurdens: "e.g. hypertension, cancer",
  height: "e.g. 170 cm",
  weight: "e.g. 70 kg",
};
